import {
  canBeChildOf,
  DEPARTMENT_TYPE_LABELS,
  DEPARTMENT_TYPES,
  departmentInputSchema,
  type Department,
  type DepartmentInput,
  type DepartmentTypeName,
} from '@fluxus/shared';
import { useId, useMemo, useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { FormAlert, FormField, fieldA11y } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import { errorMessage } from '@/lib/api/errors';
import { apiFieldErrors, hasFieldErrors, zodFieldErrors, type FieldErrors } from '@/lib/forms';

/** Caminho legível de uma área ("Diretoria Comercial › Gerência de Vendas"). */
export function departmentPath(dep: Department, byId: Map<string, Department>): string {
  const names: string[] = [];
  let current: Department | undefined = dep;
  const seen = new Set<string>();
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    names.unshift(current.name);
    current = current.parentId ? byId.get(current.parentId) : undefined;
  }
  return names.join(' › ');
}

/** Tipo de uma área nova: o nível logo abaixo da superior (ou Diretoria, no topo). */
function typeBelow(parent: DepartmentTypeName | undefined): DepartmentTypeName {
  if (!parent) return 'DIRECTORATE';
  return DEPARTMENT_TYPES.find((t) => canBeChildOf(t, parent)) ?? 'DEPARTMENT';
}

/** Ids da área e de todas as que estão abaixo dela (não podem ser escolhidas como superior). */
function descendantsOf(id: string, all: Department[]): Set<string> {
  const out = new Set<string>([id]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const d of all) {
      if (d.parentId && out.has(d.parentId) && !out.has(d.id)) {
        out.add(d.id);
        grew = true;
      }
    }
  }
  return out;
}

type Props = {
  departments: Department[];
  initial?: Department;
  /** Área superior sugerida ao criar (ex.: "Nova área abaixo de…"). */
  defaultParentId?: string | null;
  submitLabel: string;
  onSubmit: (input: DepartmentInput) => Promise<unknown>;
  onCancel: () => void;
  onBusyChange?: (busy: boolean) => void;
};

/** Área da empresa: nome, tipo e área superior (só as de nível acima e ativas aparecem). */
export function DepartmentForm({
  departments,
  initial,
  defaultParentId,
  submitLabel,
  onSubmit,
  onCancel,
  onBusyChange,
}: Props) {
  const [name, setName] = useState(initial?.name ?? '');
  const [type, setType] = useState<DepartmentTypeName>(
    () => initial?.type ?? typeBelow(departments.find((d) => d.id === defaultParentId)?.type),
  );
  const [parentId, setParentId] = useState(initial?.parentId ?? defaultParentId ?? '');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const ids = { name: useId(), type: useId(), parent: useId() };

  const byId = useMemo(() => new Map(departments.map((d) => [d.id, d])), [departments]);
  const blocked = useMemo(
    () => (initial ? descendantsOf(initial.id, departments) : new Set<string>()),
    [initial, departments],
  );
  const parentOptions = departments
    .filter((d) => !d.archivedAt && !blocked.has(d.id) && canBeChildOf(type, d.type))
    .map((d) => ({ id: d.id, path: departmentPath(d, byId), type: d.type }))
    // Pela trilha: a área superior aparece antes das que estão abaixo dela.
    .sort((a, b) => a.path.localeCompare(b.path, 'pt-BR'))
    .map((o) => ({ id: o.id, label: `${o.path} (${DEPARTMENT_TYPE_LABELS[o.type]})` }));
  const parentStillValid = !parentId || parentOptions.some((o) => o.id === parentId);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    setFormError(null);
    const parsed = departmentInputSchema.safeParse({ name, type, parentId: parentId || null });
    const next = parsed.success ? {} : zodFieldErrors(parsed.error);
    if (!parentStillValid) next.parentId = 'Escolha uma área superior de nível acima.';
    if (!parsed.success || hasFieldErrors(next)) {
      setErrors(next);
      return;
    }
    setErrors({});
    setSaving(true);
    onBusyChange?.(true);
    try {
      await onSubmit(parsed.data);
    } catch (err) {
      const fields = apiFieldErrors(err);
      if (hasFieldErrors(fields)) setErrors(fields);
      else setFormError(err);
    } finally {
      setSaving(false);
      onBusyChange?.(false);
    }
  }

  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-5">
      <FormField id={ids.name} label="Nome da área" error={errors.name}>
        <Input
          {...fieldA11y(ids.name, errors.name)}
          value={name}
          maxLength={120}
          placeholder="Ex.: Diretoria Comercial"
          onChange={(e) => {
            setName(e.target.value);
            setErrors((x) => ({ ...x, name: undefined }));
          }}
          className="h-11"
        />
      </FormField>
      <FormField id={ids.type} label="Tipo" error={errors.type}>
        <NativeSelect
          {...fieldA11y(ids.type, errors.type)}
          value={type}
          onChange={(e) => {
            setType(e.target.value as DepartmentTypeName);
            setErrors((x) => ({ ...x, type: undefined, parentId: undefined }));
          }}
        >
          {DEPARTMENT_TYPES.map((t) => (
            <option key={t} value={t}>
              {DEPARTMENT_TYPE_LABELS[t]}
            </option>
          ))}
        </NativeSelect>
      </FormField>
      <FormField
        id={ids.parent}
        label="Área superior"
        error={
          errors.parentId ?? (parentStillValid ? undefined : 'Esta área não pode ficar abaixo da escolhida.')
        }
      >
        <NativeSelect
          {...fieldA11y(ids.parent, errors.parentId ?? (parentStillValid ? undefined : 'inválida'))}
          value={parentId}
          onChange={(e) => {
            setParentId(e.target.value);
            setErrors((x) => ({ ...x, parentId: undefined }));
          }}
        >
          <option value="">Nenhuma (topo da estrutura)</option>
          {!parentStillValid && parentId && byId.get(parentId) ? (
            <option value={parentId}>{departmentPath(byId.get(parentId)!, byId)}</option>
          ) : null}
          {parentOptions.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </NativeSelect>
      </FormField>
      {formError ? <FormAlert tone="error">{errorMessage(formError)}</FormAlert> : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
          Cancelar
        </Button>
        <Button type="submit" loading={saving}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
